/* global window, AbortController */
import { Controller } from '@hotwired/stimulus'
import mapboxgl from 'mapbox-gl'

const HUB_COLOR = '#24A718'
const STREET_COLOR = '#6B7280'
const CLUSTER_MAX_ZOOM = 13
const HUB_MIN_ZOOM = 14

export default class extends Controller {
  static targets = [
    'map', 'operatorChip', 'statusChip', 'hubsOnly', 'payload',
    'kpiVehicles', 'kpiOperators', 'kpiHubs', 'kpiDropZones', 'kpiLowBattery'
  ]

  static values = { fetchUrl: String }

  connect () {
    this.operators = new Set()
    this.statuses = new Set()
    this.hubsOnly = false
    this.payload = null
    this.mapReady = false
    this.sourcesReady = false
    this.abortController = new AbortController()
    this.initMap()
    if (this.hasPayloadTarget && this.payloadTarget.textContent.trim() !== '') {
      this.payload = JSON.parse(this.payloadTarget.textContent)
      this.render()
    } else {
      this.load()
    }
  }

  disconnect () {
    if (this.abortController) this.abortController.abort()
    if (this.map) this.map.remove()
  }

  selectOperator (event) {
    const operator = event.currentTarget.dataset.operator
    if (operator === 'all') {
      this.operators.clear()
    } else if (this.operators.has(operator)) {
      this.operators.delete(operator)
    } else {
      this.operators.add(operator)
    }
    this.syncOperatorChips()
    this.render()
  }

  syncOperatorChips () {
    this.operatorChipTargets.forEach((chip) => {
      const operator = chip.dataset.operator
      const active = operator === 'all' ? this.operators.size === 0 : this.operators.has(operator)
      chip.classList.toggle('is-active', active)
      chip.setAttribute('aria-pressed', active ? 'true' : 'false')
    })
  }

  toggleStatus (event) {
    const status = event.currentTarget.dataset.status
    const pressed = this.statuses.has(status)
    if (pressed) {
      this.statuses.delete(status)
    } else {
      this.statuses.add(status)
    }
    event.currentTarget.classList.toggle('is-active', !pressed)
    event.currentTarget.setAttribute('aria-pressed', pressed ? 'false' : 'true')
    this.render()
  }

  toggleHubs (event) {
    this.hubsOnly = !this.hubsOnly
    event.currentTarget.classList.toggle('is-active', this.hubsOnly)
    event.currentTarget.setAttribute('aria-pressed', this.hubsOnly ? 'true' : 'false')
    this.render()
  }

  initMap () {
    mapboxgl.accessToken = window.mapbox_data.access_token
    const center = window.mapbox_data.coordinates || [-118.2437, 34.0522]
    this.map = new mapboxgl.Map({
      container: this.mapTarget,
      style: 'mapbox://styles/mapbox/streets-v12',
      center,
      zoom: 12
    })
    this.map.addControl(new mapboxgl.NavigationControl(), 'bottom-right')
    this.map.on('load', () => {
      this.mapReady = true
      this.render()
    })
  }

  async load () {
    try {
      const response = await fetch(this.fetchUrlValue, {
        headers: { Accept: 'application/json' },
        signal: this.abortController.signal
      })
      this.payload = await response.json()
      this.render()
    } catch (error) {
      if (error.name === 'AbortError') return
      throw error
    }
  }

  render () {
    this.renderKpis()
    if (!this.mapReady || !this.payload) return
    this.ensureSources()
    this.map.getSource('hubs').setData(this.hubCollection())
    this.map.getSource('hub-centers').setData(this.hubCenterCollection())
    this.map.getSource('vehicles').setData(this.vehicleCollection())
    this.map.getSource('stations').setData(this.stationCollection())

    const showStations = this.stationsVisible()
    const showVehicles = this.operatorSelected('bird_la') || this.operatorSelected('veo_la')
    this.setVisibility('vehicles', showVehicles)
    this.setVisibility('vehicle-clusters', showVehicles)
    this.setVisibility('vehicle-cluster-count', showVehicles)
    this.setVisibility('stations', showStations)
    this.setVisibility('station-clusters', showStations)
    this.setVisibility('station-cluster-count', showStations)

    if (!this.fitted) {
      this.fitted = true
      this.fitToData()
    }
  }

  ensureSources () {
    if (this.sourcesReady) return

    this.map.addSource('hubs', { type: 'geojson', data: this.emptyCollection() })
    this.map.addSource('hub-centers', { type: 'geojson', data: this.emptyCollection() })
    this.map.addSource('vehicles', {
      type: 'geojson',
      data: this.emptyCollection(),
      cluster: true,
      clusterMaxZoom: CLUSTER_MAX_ZOOM,
      clusterRadius: 40
    })
    this.map.addSource('stations', {
      type: 'geojson',
      data: this.emptyCollection(),
      cluster: true,
      clusterMaxZoom: CLUSTER_MAX_ZOOM,
      clusterRadius: 40
    })

    this.map.addLayer({
      id: 'hub-rings',
      type: 'line',
      source: 'hubs',
      minzoom: HUB_MIN_ZOOM,
      paint: {
        'line-color': HUB_COLOR,
        'line-width': 2,
        'line-dasharray': [2, 2]
      }
    })
    this.map.addLayer({
      id: 'hub-labels',
      type: 'symbol',
      source: 'hub-centers',
      minzoom: HUB_MIN_ZOOM,
      layout: {
        'text-field': ['concat', 'Hub: ', ['get', 'name']],
        'text-size': 12,
        'text-offset': [0, -1.2],
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Regular']
      },
      paint: { 'text-color': HUB_COLOR }
    })
    this.map.addLayer({
      id: 'vehicle-clusters',
      type: 'circle',
      source: 'vehicles',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': STREET_COLOR,
        'circle-radius': ['step', ['get', 'point_count'], 14, 20, 18, 100, 24],
        'circle-opacity': 0.85
      }
    })
    this.map.addLayer({
      id: 'vehicle-cluster-count',
      type: 'symbol',
      source: 'vehicles',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12
      },
      paint: { 'text-color': '#ffffff' }
    })
    this.map.addLayer({
      id: 'vehicles',
      type: 'circle',
      source: 'vehicles',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-radius': 5,
        'circle-color': ['case', ['==', ['get', 'in_hub'], true], HUB_COLOR, STREET_COLOR],
        'circle-stroke-width': 1,
        'circle-stroke-color': '#ffffff'
      }
    })
    this.map.addLayer({
      id: 'station-clusters',
      type: 'circle',
      source: 'stations',
      filter: ['has', 'point_count'],
      layout: { visibility: 'none' },
      paint: {
        'circle-color': STREET_COLOR,
        'circle-radius': ['step', ['get', 'point_count'], 14, 20, 18, 100, 24],
        'circle-opacity': 0.85
      }
    })
    this.map.addLayer({
      id: 'station-cluster-count',
      type: 'symbol',
      source: 'stations',
      filter: ['has', 'point_count'],
      layout: {
        visibility: 'none',
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12
      },
      paint: { 'text-color': '#ffffff' }
    })
    this.map.addLayer({
      id: 'stations',
      type: 'circle',
      source: 'stations',
      filter: ['!', ['has', 'point_count']],
      layout: { visibility: 'none' },
      paint: {
        'circle-radius': 5,
        'circle-color': STREET_COLOR,
        'circle-stroke-width': 1,
        'circle-stroke-color': '#ffffff'
      }
    })

    this.sourcesReady = true
  }

  setVisibility (layerId, visible) {
    this.map.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none')
  }

  vehicleCollection () {
    return {
      type: 'FeatureCollection',
      features: this.filteredVehicles().map((vehicle) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [vehicle.longitude, vehicle.latitude] },
        properties: { in_hub: vehicle.in_hub === true }
      }))
    }
  }

  operatorSelected (operator) {
    return this.operators.size === 0 || this.operators.has(operator)
  }

  stationsVisible () {
    if (!this.operatorSelected('metro_bike_share')) return false
    if (this.operators.size === 0 && (this.hubsOnly || this.statuses.size > 0)) return false
    return true
  }

  stationCollection () {
    const stations = this.stationsVisible() ? (this.payload.stations || []) : []
    return {
      type: 'FeatureCollection',
      features: stations.map((station) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [station.longitude, station.latitude] },
        properties: { name: station.name || 'Station' }
      }))
    }
  }

  hubCollection () {
    return {
      type: 'FeatureCollection',
      features: (this.payload.hubs || []).map((hub) => ({
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [circlePolygon(hub.longitude, hub.latitude, hub.radius_meters || 75)]
        },
        properties: { name: hub.name }
      }))
    }
  }

  hubCenterCollection () {
    return {
      type: 'FeatureCollection',
      features: (this.payload.hubs || []).map((hub) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [hub.longitude, hub.latitude] },
        properties: { name: hub.name }
      }))
    }
  }

  filteredVehicles () {
    return (this.payload.vehicles || []).filter((vehicle) => {
      if (!this.operatorSelected(vehicle.feed_key)) return false
      if (this.hubsOnly && vehicle.in_hub !== true) return false
      if (this.statuses.size === 0) return true
      return Array.from(this.statuses).some((status) => matchesStatus(vehicle, status))
    })
  }

  renderKpis () {
    if (!this.payload || !this.hasKpiVehiclesTarget) return

    const vehicles = this.filteredVehicles()
    this.kpiVehiclesTarget.textContent = formatCount(vehicles.length)
    this.kpiOperatorsTarget.textContent = formatCount(this.operatorsReporting(vehicles))
    this.kpiHubsTarget.textContent = formatCount(vehicles.filter((vehicle) => vehicle.in_hub === true).length)
    this.kpiDropZonesTarget.textContent = formatCount(vehicles.filter((vehicle) => vehicle.in_drop_zone === true).length)
    this.kpiLowBatteryTarget.textContent = formatCount(
      vehicles.filter((vehicle) => vehicle.in_hub === true && vehicle.low_battery === true).length
    )
  }

  operatorsReporting (vehicles) {
    const reporting = (this.payload.operators || []).filter((operator) => (
      operator.kind !== 'static' &&
      operator.status === 'up' &&
      this.operatorSelected(operator.key)
    ))
    if (!this.hubsOnly && this.statuses.size === 0) return reporting.length

    const present = new Set(vehicles.map((vehicle) => vehicle.feed_key))
    return reporting.filter((operator) => present.has(operator.key)).length
  }

  fitToData () {
    const points = []
    this.filteredVehicles().forEach((vehicle) => points.push([vehicle.longitude, vehicle.latitude]))
    if (this.stationsVisible()) {
      (this.payload.stations || []).forEach((station) => points.push([station.longitude, station.latitude]))
    }
    if (points.length === 0) {
      (this.payload.hubs || []).forEach((hub) => points.push([hub.longitude, hub.latitude]))
    }
    if (points.length === 0) return

    const bounds = points.reduce((box, point) => box.extend(point), new mapboxgl.LngLatBounds(points[0], points[0]))
    this.map.fitBounds(bounds, { padding: 48, maxZoom: 15, duration: 0 })
  }

  emptyCollection () {
    return { type: 'FeatureCollection', features: [] }
  }
}

function formatCount (value) {
  return Number(value).toLocaleString('en-US')
}

function matchesStatus (vehicle, status) {
  if (status === 'available') return !vehicle.is_reserved && !vehicle.is_disabled
  if (status === 'reserved') return vehicle.is_reserved === true
  if (status === 'disabled') return vehicle.is_disabled === true
  return false
}

function circlePolygon (longitude, latitude, radiusMeters, steps = 64) {
  const earthRadius = 6371008.8
  const angular = radiusMeters / earthRadius
  const lat = latitude * Math.PI / 180
  const lng = longitude * Math.PI / 180
  const ring = []

  for (let step = 0; step <= steps; step += 1) {
    const bearing = (step / steps) * 2 * Math.PI
    const lat2 = Math.asin(
      Math.sin(lat) * Math.cos(angular) +
      Math.cos(lat) * Math.sin(angular) * Math.cos(bearing)
    )
    const lng2 = lng + Math.atan2(
      Math.sin(bearing) * Math.sin(angular) * Math.cos(lat),
      Math.cos(angular) - Math.sin(lat) * Math.sin(lat2)
    )
    ring.push([lng2 * 180 / Math.PI, lat2 * 180 / Math.PI])
  }

  return ring
}
